/**
 * Lightweight context that hands the active project's absolute ``cwd``
 * down to leaf block components.
 *
 * Block views render in isolation in Storybook, in unit tests, and in
 * the live chat — they should not pull the heavy
 * :class:`AgentPanelContext` just to look up the project path. This
 * tiny context gives them an opt-in hook (defaults to ``null``) so
 * formatters like :func:`relativizePath` can produce
 * ``packages/webapp/src/main.tsx`` instead of an absolute mouthful,
 * while stories that don't provide a value still render fine with
 * the original full path.
 */

import * as React from 'react';

const ProjectCwdContext = React.createContext<string | null>(null);

/** Hook accessor — returns the active project ``cwd`` or ``null`` outside a provider. */
export function useProjectCwd(): string | null {
  return React.useContext(ProjectCwdContext);
}

export interface ProjectCwdProviderProps {
  cwd: string | null;
  children: React.ReactNode;
}

/**
 * Wrap a chat region with a known project ``cwd``. Currently mounted
 * by :class:`AgentPanelOverlay` so every block under the chat sees
 * the same value.
 */
export function ProjectCwdProvider({ cwd, children }: ProjectCwdProviderProps) {
  return <ProjectCwdContext.Provider value={cwd}>{children}</ProjectCwdContext.Provider>;
}
